import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/axios';

interface Notif {
  id: string;
  title: string | null;
  bodyPreview: string;
  readAt: string | null;
  createdAt: string;
}

export function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<Notif[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get<{ data: { count: number } }>('/dealer/notifications/unread-count');
      setUnread(r.data.data.count);
    } catch {
      /* ignore (e.g. not yet authenticated) */
    }
  }, []);

  useEffect(() => {
    void loadCount();
    const id = setInterval(loadCount, 60_000);
    return () => clearInterval(id);
  }, [loadCount]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const togglePanel = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    try {
      const r = await api.get<{ data: { rows: Notif[] } }>('/dealer/notifications?limit=15');
      setItems(r.data.data.rows);
      // Mark as read on view.
      if (unread > 0) {
        await api.post('/dealer/notifications/read', {});
        setUnread(0);
      }
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={togglePanel} title={t('notifications.title')} className="icon-btn bell-btn">
        <svg className="icon icon-lg" aria-hidden="true">
          <use href="#i-bell" />
        </svg>
        {unread > 0 && <span className="bell-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-panel" style={{ position: 'absolute', left: 0, marginTop: 8, maxHeight: 384, overflowY: 'auto', zIndex: 50 }}>
          <div className="notif-head">{t('notifications.title')}</div>
          {items.length === 0 ? (
            <div className="state-block" style={{ padding: '40px 14px' }}>{t('notifications.empty')}</div>
          ) : (
            items.map((n) => (
              <div key={n.id} className={`notif-row${n.readAt ? '' : ' is-unread'}`}>
                {n.title && <div className="nr-title">{n.title}</div>}
                <div className="nr-body">{n.bodyPreview}</div>
                <div className="nr-time">{new Date(n.createdAt).toLocaleString()}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
